import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import LOGO from "../assets/images/logo.png";
import { appVersion } from "../components/global";
import { GET, POST, alert, loading, tokens, clearAuthStorage } from "../components/CustomComponent";

const REMEMBER_EMAIL_KEY = "ptn-remember-email";

export default function Login() {
    // อีเมลที่เคยติ๊ก "จดจำอีเมล" ไว้ (clearAuthStorage เก็บ key นี้ไว้ให้ ไม่ล้างตอน logout)
    const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_EMAIL_KEY) || "");
    const [password, setPassword] = useState("");
    const [rememberEmail, setRememberEmail] = useState(() => !!localStorage.getItem(REMEMBER_EMAIL_KEY));
    const [showPassword, setShowPassword] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotSent, setForgotSent] = useState(false);
    const [forgotSending, setForgotSending] = useState(false);
    // กันกดเข้าสู่ระบบซ้ำระหว่างรอผล (กดรัว ๆ จะได้หลาย session / หลายคำขอซ้อนกัน)
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isReady = email.trim() !== "" && password.trim() !== "" && !isSubmitting;

    // ขอลิงก์ตั้งรหัสผ่านใหม่
    // backend ตอบ success เสมอแม้ไม่มีอีเมลนี้ในระบบ (กันใช้เส้นนี้ไล่เดาว่าอีเมลไหนมีอยู่)
    const handleForgotPassword = async () => {
        if (forgotSending) return;

        const trimmedEmail = forgotEmail.trim();
        if (!trimmedEmail) {
            alert("", "warning", "แจ้งเตือน", "กรุณากรอกอีเมล");
            return;
        }

        setForgotSending(true);
        loading(true);
        try {
            const result = await POST("v1/auth/forgot-password", { email: trimmedEmail });

            if (result?.status) {
                setForgotSent(true);
            } else {
                alert("", "warning", "แจ้งเตือน", result?.message || "ส่งคำขอไม่สำเร็จ");
            }
        } finally {
            loading(false);
            setForgotSending(false);
        }
    };

    const openForgotModal = () => {
        setForgotEmail(email.trim());
        setForgotSent(false);
        setShowForgot(true);
    };

    useEffect(() => {
        let isMounted = true;

        // มี token ค้างอยู่ → ลองดึงโปรไฟล์
        // - สำเร็จ: เข้าระบบต่อได้เลย
        // - 401: token ใช้ไม่ได้แล้วจริง ๆ ค่อยล้าง localStorage (เก็บ mode / remembered email ไว้)
        // - network / server error: ไม่ล้าง ผู้ใช้ยังถือ session เดิมไว้ พอ backend กลับมาก็ใช้ต่อได้
        const getProfile = async () => {
            if (tokens) {
                const result = await GET("v1/admin/users/profile", null, null, { silent: true });

                if (!isMounted) {
                    return;
                }

                if (result?.status === true) {
                    window.location.href = "/welcome";
                    return;
                }

                if (result?.status_code === 401) {
                    clearAuthStorage();
                }
            }

            loading(false);
        };

        getProfile();

        return () => {
            isMounted = false;
        };
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isSubmitting) return;

        const trimmedEmail = email.trim();
        const trimmedPassword = password.trim();

        if (!trimmedEmail) {
            alert("", "warning", "แจ้งเตือน", "กรุณากรอกอีเมล");
            return;
        }

        if (!trimmedPassword) {
            alert("", "warning", "แจ้งเตือน", "กรุณากรอกรหัสผ่าน");
            return;
        }

        setIsSubmitting(true);
        loading(true);

        try {
            const result = await POST("v1/auth/login", {
                email: trimmedEmail,
                password: btoa(trimmedPassword),
            });

            if (result?.status === true && result?.data?.token) {
                localStorage.setItem("ptn-acc-tk", btoa(`Bearer ${result.data.token}`));
                const profileResult = await GET("v1/admin/users/profile");
                
                if (!profileResult?.status) {
                    alert("", "warning", "แจ้งเตือน", profileResult?.message || "ดึงข้อมูลผู้ใช้ไม่สำเร็จ");
                    return;
                }

                localStorage.setItem("ptn-profile", JSON.stringify(profileResult.data));
                if (rememberEmail) {
                    localStorage.setItem(REMEMBER_EMAIL_KEY, trimmedEmail);
                } else {
                    localStorage.removeItem(REMEMBER_EMAIL_KEY);
                }
                window.location.href = "/welcome";
                return;
            }

            alert("", "warning", "แจ้งเตือน", result?.message || "เข้าสู่ระบบไม่สำเร็จ");
        } catch (error) {
            console.error("Login error:", error);
            alert("", "error", "ผิดพลาด", "เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่อีกครั้ง");
        } finally {
            loading(false);
            setIsSubmitting(false);
        } 
    };

    return (
      <section className="login-page">
          <div className="login-aurora login-aurora--a" aria-hidden="true" />
          <div className="login-aurora login-aurora--b" aria-hidden="true" />

          <div className="login-shell">
              <div className="login-brand">
                  <img src={LOGO} alt="ParthenonTech logo" className="login-brand-logo" />
                  <div className="login-brand-rule" />
                  <p className="login-brand-eyebrow">Administration Portal</p>
                  <h1 className="login-brand-title">
                      บริหารองค์กรครบ<br />ในที่เดียวกับ <strong>ParthenonTech</strong>
                  </h1>
                  <p className="login-brand-text">
                      ดูแลโปรเจค บุคลากร และการเงินของบริษัทได้อย่างเป็นระบบ ปลอดภัย และตรวจสอบได้ทุกขั้นตอน
                  </p>
                  <ul className="login-brand-pillars">
                      <li><i className="fa-solid fa-diagram-project" />Projects</li>
                      <li><i className="fa-solid fa-users" />Workforce</li>
                      <li><i className="fa-solid fa-coins" />Finance</li>
                  </ul>
              </div>

              <div className="login-card">
                  <p className="login-card-eyebrow">Welcome back</p>
                  <h2 className="login-card-title">เข้าสู่ระบบ</h2>
                  <p className="login-card-subtitle">Sign in to continue to ParthenonTech</p>

                  <form className="login-form" onSubmit={handleSubmit}>
                      <div>
                          <div className="login-label-row">
                              <label htmlFor="email" className="login-label">Email</label>
                          </div>
                          <div className="login-field">
                              <i className="fa-regular fa-envelope login-field-icon" />
                              <input
                                  id="email"
                                  type="email"
                                  inputMode="email"
                                  value={email}
                                  onChange={(event) => setEmail(event.target.value)}
                                  className="login-input"
                                  placeholder="name@parthenontech.com"
                                  autoComplete="username"
                                  autoCapitalize="none"
                                  spellCheck={false}
                              />
                          </div>
                      </div>

                      <div>
                          <div className="login-label-row">
                              <label htmlFor="password" className="login-label">Password</label>
                              <button type="button" onClick={openForgotModal} className="login-link">
                                  ลืมรหัสผ่าน?
                              </button>
                          </div>
                          <div className="login-field">
                              <i className="fa-solid fa-lock login-field-icon" />
                              <input
                                  id="password"
                                  type={showPassword ? "text" : "password"}
                                  value={password}
                                  onChange={(event) => setPassword(event.target.value)}
                                  className="login-input"
                                  placeholder="Enter your password"
                                  autoComplete="current-password"
                              />
                              <button
                                  type="button"
                                  className="login-field-toggle"
                                  onClick={() => setShowPassword((value) => !value)}
                                  aria-label={showPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                                  aria-pressed={showPassword}
                              >
                                  <i className={showPassword ? "fa-regular fa-eye-slash" : "fa-regular fa-eye"} />
                              </button>
                          </div>
                      </div>

                      <label className="login-remember" htmlFor="remember-me">
                          <input
                              type="checkbox"
                              id="remember-me"
                              checked={rememberEmail}
                              onChange={(event) => setRememberEmail(event.target.checked)}
                          />
                          จดจำอีเมลของฉัน
                      </label>

                      <button type="submit" disabled={!isReady} className="login-submit">
                          {isSubmitting ? (
                              <>
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                  กำลังเข้าสู่ระบบ...
                              </>
                          ) : (
                              <>
                                  เข้าสู่ระบบ
                                  <i className="fa-solid fa-arrow-right" />
                              </>
                          )}
                      </button>
                  </form>

                  <div className="login-card-foot">
                      <i className="fa-solid fa-shield-halved" />
                      Secured connection · Version {appVersion}
                  </div>
              </div>
          </div>

          <p className="login-copyright">© {new Date().getFullYear()} Parthenon Tech Co., Ltd.</p>

          {/* ลืมรหัสผ่าน: ขอลิงก์ตั้งรหัสผ่านใหม่ทางอีเมล */}
          <Modal show={showForgot} onHide={() => setShowForgot(false)} centered dialogClassName="login-forgot-modal">
              <Modal.Header closeButton className="border-0">
                  <Modal.Title className="fw-bold h5 mb-0" style={{ color: "#0b2c4c" }}>ลืมรหัสผ่าน</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                  {forgotSent ? (
                      <div className="text-center py-3">
                          <div
                              className="d-inline-flex align-items-center justify-content-center mb-3"
                              style={{ width: 64, height: 64, borderRadius: 20, background: "rgba(47, 181, 114, 0.14)", color: "#188251", fontSize: 26 }}
                          >
                              <i className="fa-solid fa-envelope-circle-check" />
                          </div>
                          <div className="fw-semibold mb-2" style={{ color: "#0b2c4c" }}>ส่งลิงก์ให้แล้ว</div>
                          <div className="text-secondary small">
                              ถ้ามีบัญชีที่ใช้อีเมลนี้อยู่ในระบบ จะได้รับลิงก์ตั้งรหัสผ่านใหม่ภายในไม่กี่นาที
                              <br />ลิงก์ใช้ได้ 30 นาที และใช้ได้ครั้งเดียว
                          </div>
                      </div>
                  ) : (
                      <>
                          <div className="text-secondary small mb-3">
                              กรอกอีเมลที่ใช้เข้าสู่ระบบ ระบบจะส่งลิงก์สำหรับตั้งรหัสผ่านใหม่ไปให้
                          </div>
                          <label className="form-label text-secondary small mb-1">อีเมล</label>
                          <input
                              type="email"
                              className="form-control rounded-3"
                              value={forgotEmail}
                              onChange={(event) => setForgotEmail(event.target.value)}
                              onKeyDown={(event) => event.key === "Enter" && handleForgotPassword()}
                              placeholder="you@company.com"
                              autoComplete="email"
                          />
                      </>
                  )}
              </Modal.Body>
              <Modal.Footer className="border-0">
                  <button className="btn btn-secondary px-4 rounded-3" onClick={() => setShowForgot(false)}>ปิด</button>
                  {!forgotSent && (
                      <button className="btn btn-primary px-4 rounded-3" onClick={handleForgotPassword} disabled={forgotSending}>
                          {forgotSending ? "กำลังส่ง..." : "ส่งลิงก์"}
                      </button>
                  )}
              </Modal.Footer>
          </Modal>
      </section>
  );
}
